import { useState } from "react";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError, setToken } from "@/lib/adminApi";

const FIELD = "w-full bg-transparent border-b border-[#C9BFA8] focus:border-[#8a6b2f] outline-none py-3 text-navy transition-colors";
const LABEL = "font-mono text-[10px] uppercase tracking-[0.24em] text-ink";
const EMPTY = { current: "", next: "", confirm: "" };

export const PasscodePanel = ({ onAuthError }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const update = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.next.length < 8) { toast.error("New passcode must be at least 8 characters."); return; }
    if (f.next !== f.confirm) { toast.error("New passcodes don't match."); return; }
    setSaving(true);
    try {
      const { data } = await adminApi.post("/admin/passcode", { current: f.current, new: f.next });
      setToken(data.token); // other devices/sessions are signed out; this one continues
      setF(EMPTY);
      toast.success("Passcode updated. Use the new one next time you unlock.");
    } catch (err) {
      if (err?.response?.status === 401 && !String(apiError(err)).includes("passcode")) onAuthError();
      else toast.error(apiError(err));
    } finally { setSaving(false); }
  };

  return (
    <section data-testid="admin-passcode-panel" className="bg-cream-ivory border border-[#D9CFB8] p-8 lg:p-10">
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8a6b2f] mb-2">Security</p>
      <h2 className="font-serif text-3xl text-navy flex items-center gap-3"><KeyRound size={22} className="text-[#8a6b2f]" /> Change owner passcode</h2>
      <p className="text-ink-muted text-sm mt-3 max-w-lg">Changing it signs out any other open sessions. Keep it at least 8 characters.</p>

      <form onSubmit={submit} className="mt-8 grid sm:grid-cols-3 gap-8 max-w-3xl" noValidate>
        <div>
          <label className={LABEL} htmlFor="pc-current">Current passcode</label>
          <input id="pc-current" type="password" autoComplete="current-password" data-testid="passcode-current-input" className={FIELD} value={f.current} onChange={update("current")} />
        </div>
        <div>
          <label className={LABEL} htmlFor="pc-next">New passcode</label>
          <input id="pc-next" type="password" autoComplete="new-password" data-testid="passcode-new-input" className={FIELD} value={f.next} onChange={update("next")} />
        </div>
        <div>
          <label className={LABEL} htmlFor="pc-confirm">Confirm new passcode</label>
          <input id="pc-confirm" type="password" autoComplete="new-password" data-testid="passcode-confirm-input" className={FIELD} value={f.confirm} onChange={update("confirm")} />
        </div>
        <div className="sm:col-span-3">
          <button type="submit" disabled={saving || !f.current || !f.next || !f.confirm} data-testid="passcode-save-btn"
            className="bg-navy text-parchment px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] border border-gold/30 hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:translate-y-0">
            {saving ? "Updating…" : "Update passcode"}
          </button>
        </div>
      </form>
    </section>
  );
};
